import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  IFacilityImpactSource,
  SourceGroupBy,
  useFacilityImpactPerSource,
} from "@/pages/dashboard/hooks/useFacilityImpact";
import { formatAmountWithUnit } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { unitMapping } from "@/utilities/units";
import { useEffect } from "react";
import { facilityIcon, facilityLabel } from "../FacilityImpactTopContributors";

export default function FacilityImpactPerFacility({
  metric,
  groupBy,
}: {
  groupBy: SourceGroupBy;
  metric: ImpactMetric;
}) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 10 },
      defaultSort: { key: metric.key, order: "descend" },
    });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const columns: Column<IFacilityImpactSource>[] = [
    {
      label: "",
      key: "icon",
      maxWidth: "50px",
      minWidth: "50px",
      renderer: (record) => facilityIcon(record.type),
    },
    {
      label: "Source",
      key: "utility_source_label",
      sortable: true,
      maxWidth: "1fr",
    },
    {
      label: "Usage",
      key: "amount",
      renderer: (record) =>
        formatAmountWithUnit(record.amount, unitMapping[record.unit] || record.unit),
    },
    {
      label: "Type",
      key: "type",
      searchable: true,
      sortable: true,
      renderer: (record) => facilityLabel(record.type),
    },
    {
      label: "Facility",
      key: "facilities",
      searchable: true,
      renderer: (record) =>
        record.facilities.length === 1 ? (
          <CrossReference to={`/facilities/${record.facilities[0].id}`}>
            {record.facilities[0].name}
          </CrossReference>
        ) : (
          record.facilities.length + " facilities"
        ),
    },
    {
      label: "Share",
      key: "share",
      maxWidth: "1fr",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record.impact[metric.key]}
            value={data?.total_impact[metric.key]}
          />
        );
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record) => metric.formatter(record.impact[metric.key]),
    },
  ];

  const { data, isLoading, isFetching } = useFacilityImpactPerSource({
    pagination,
    filter,
    search,
    sort,
    groupBy,
    metric: metric.key,
  });

  return (
    <Table
      hideRaster={true}
      type={"grey"}
      filter={filter}
      sort={sort}
      search={search}
      columns={columns}
      data={data?.sources}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      totalRows={data?.total_rows}
      rowKey={"key"}
    />
  );
}
